import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAccount, useMsal } from '@azure/msal-react';
import { loginRequest, pricerApiV2Request } from '../authConfig';
import * as signalR from '@microsoft/signalr';
import { appInsights } from '../Utils/appInsights';
import * as uuid from 'uuid';
import useAuthToken from './useAuthToken';
import { PRICING_REQUEST_TIME, PRICING_REQUEST_PAUSED_TIME, LONG_RUNNING_PRICING_REQUEST_TIME } from '../Constants/metricName';
import { getAuthenticatedClient } from '../Utils/tokenHelper';
import { enableFrontdoor } from '../Utils/features';

const BASE_URL = enableFrontdoor ? process.env.REACT_APP_ASYNC_PRICER_API_URL_FRONTDOOR : process.env.REACT_APP_ASYNC_PRICER_API_URL;

export default function useAsyncPricerApi(modelType, disableMetrics) {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [requestId, setRequestId] = useState(null);
    const [connected, setConnected] = useState(false);
    const [receivedResults, setReceivedResults] = useState([]);
    const { instance, accounts } = useMsal();
    const [pausedRequest, setPausedRequest] = useState(null);
    const account = useAccount(accounts[0]);
    const { objectId } = useAuthToken();
    const [startTime, setStartTime] = useState();
    const [metricCorrelationId, setMetricCorrelationId] = useState();

    useEffect(() => {
        let lockResolver;
        if (navigator && navigator.locks && navigator.locks.request) {
            const promise = new Promise((res) => {
                lockResolver = res;
            });

            navigator.locks.request(uuid.v4(), { mode: "shared" }, () => {
                return promise;
            });
        }

        return () => {
            if (lockResolver) {
                lockResolver();
            }
        }
    }, []);

    useEffect(() => {
        if (!requestId || receivedResults.length === 0) {
            return;
        }

        const matchingResult = receivedResults.find(r => r.requestId === requestId);
        if (matchingResult) {
            if (matchingResult.status === 'Successful') {
                setResult(matchingResult.data);
                setSuccess(true);
            } else {
                setError(matchingResult.data);
            }

            if (!disableMetrics) {
                appInsights.trackMetric({
                    name: PRICING_REQUEST_TIME,
                    average: new Date().getTime() - startTime
                }, {
                    objectId: objectId || 'unknown',
                    modelType,
                    correlationId: metricCorrelationId
                });
            }

            setLoading(false);
            // we found our result stop watching
            setReceivedResults([]);
            setRequestId(null);
        }
    }, [requestId, receivedResults]);

    useEffect(() => {
        if (disableMetrics || !metricCorrelationId || !loading || !startTime) {
            return;
        }

        const inProgressCheck = setInterval(() => {
            if (!loading || !metricCorrelationId) {
                return;
            }

            appInsights.trackMetric({
                name: LONG_RUNNING_PRICING_REQUEST_TIME,
                average: new Date().getTime() - startTime
            }, {
                objectId: objectId || 'unknown',
                modelType,
                correlationId: metricCorrelationId,
                onLine: window.navigator.onLine
            });
        }, 61000); // give it 61 seconds to help cut down on any unnecessary noise

        return () => {
            clearInterval(inProgressCheck);
        };
    }, [metricCorrelationId, loading, startTime, disableMetrics]);

    useEffect(() => {
        if (account && account.username) {
            let connection = null;
            const tokenRequest = {
                ...pricerApiV2Request,
                account,
            };

            let connectCount = 0;
            let connectTimeout = null;

            const connectToWebsocket = () => {
                instance.acquireTokenSilent(tokenRequest).then(r => {
                    const websocketBaseUrl = enableFrontdoor ? process.env.REACT_APP_WEBSOCKET_URL_FRONTDOOR : process.env.REACT_APP_WEBSOCKET_URL;
                    connection = new signalR.HubConnectionBuilder()
                        .withUrl(`${websocketBaseUrl}?accessToken=${r.accessToken}`)
                        .configureLogging(signalR.LogLevel.Error)
                        .withAutomaticReconnect()
                        .build();
                    connection.start().then(() => {
                        connection.on('PricingComplete', (data) => {
                            if (!data) {
                                return;
                            }
                            const message = JSON.parse(data);
                            setReceivedResults(r => {
                                return [
                                    ...r,
                                    message
                                ]
                            });
                        });
    
                        setConnected(true);
                    }).catch(e => {
                        // this happens if we are transitioning off the page
                        const errorMessage = e.toString(); 
                        if (errorMessage.indexOf('The connection was stopped before the hub handshake could complete.') === -1 &&
                            errorMessage.indexOf('Failed to start the HttpConnection before stop() was called.') === -1) {
                            if (connectTimeout <= 5) {
                                connectTimeout++;
                                connectTimeout = setTimeout(connectToWebsocket, 1000 * connectCount);
                            } else {
                                console.error(e);
                                setError({
                                    message: 'Unable to connect'
                                });
                            }
                        } else {
                            console.warn(e);
                        }
                    });
    
                }).catch(async e => {
                    if (e.name === 'InteractionRequiredAuthError') {
                        return instance.acquireTokenRedirect(tokenRequest);
                    } 
    
                    console.error('Failed to acquire token in pricer api', e);
                });
            };            

            connectToWebsocket();

            return () => {
                if (connection) {
                    connection.stop();
                }

                if (connectTimeout) {
                    clearTimeout(connectTimeout);
                }
            }
        }
    }, [account]);

    useEffect(async () => {
        if (connected && pausedRequest) {
            setPausedRequest(null);
            if (!disableMetrics) {
                appInsights.trackMetric({
                    name: PRICING_REQUEST_PAUSED_TIME,
                    average: new Date().getTime() - startTime
                }, {
                    objectId: objectId || 'unknown',
                    modelType,
                    correlationId: metricCorrelationId
                });
            }

            await executeRequest(pausedRequest);
        }
    }, [connected, pausedRequest]);

    const executeRequest = async (request) => {
        setLoading(true);
        setStartTime(new Date().getTime());
        setMetricCorrelationId(uuid.v4());

        if (!account) {
            console.warn('No account found, triggering a login redirect');
            await instance.loginRedirect(loginRequest);
            return;
        }

        if (!connected) {
            setPausedRequest(request);
        } else {
            setSuccess(false);
            setError(null);
            setResult(null);

            try {
                const { data } = await (await getAuthenticatedClient({
                    url: `${BASE_URL}/price`,
                    method: 'POST',
                    body: {
                        model: modelType,
                        mode: 'Live',
                        data: {
                            ...request
                        }
                    },
                    account,
                    msalInstance: instance
                }));

                setRequestId(data.requestId);
            } catch(e) {
                setError(e);
                setLoading(false);
            }
        }
    };

    const executeReset = () => {
        setLoading(false);
        setSuccess(false);
        setError(null);
        setResult(null);
        setRequestId(null);
    };

    return {
        loading,
        success,
        error,
        result,
        execute: executeRequest,
        reset: executeReset
    };
}